/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CompleteMediaUploadDto,
  CreateCaptionDto,
  CreateMediaImportDto,
  CreateMediaUploadDto,
  DeleteMediaAssetsDto,
  DownloadMediaDto,
  MediaAssetDto,
  MediaDownloadLinkDto,
  MediaImportDto,
  MediaLibraryDto,
  MediaUploadDto,
  ReplicateSceneDto,
  SaveSceneClipDto,
  SceneReplicaDto,
  TagMediaAssetsDto,
  UpdateMediaAssetDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The whole library, newest first, with storage used and allowed.
 */
export const listMedia = (id: string, signal?: AbortSignal) => {
  return customInstance<MediaLibraryDto>({
    url: `/api/v1/workspaces/${id}/media`,
    method: 'GET',
    signal,
  });
};

export const getListMediaQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/media`] as const;
};

export const getListMediaQueryOptions = <
  TData = Awaited<ReturnType<typeof listMedia>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMediaQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMedia>>> = ({ signal }) =>
    listMedia(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListMediaQueryResult = NonNullable<Awaited<ReturnType<typeof listMedia>>>;
export type ListMediaQueryError = ErrorType<ApiErrorDto>;

export function useListMedia<
  TData = Awaited<ReturnType<typeof listMedia>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMedia>>,
          TError,
          Awaited<ReturnType<typeof listMedia>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMedia<
  TData = Awaited<ReturnType<typeof listMedia>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMedia>>,
          TError,
          Awaited<ReturnType<typeof listMedia>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMedia<
  TData = Awaited<ReturnType<typeof listMedia>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The whole library, newest first, with storage used and allowed.
 */

export function useListMedia<
  TData = Awaited<ReturnType<typeof listMedia>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMedia>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMediaQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename an asset, replace its tags or change who it belongs to.
 */
export const updateMediaAsset = (
  id: string,
  assetId: string,
  updateMediaAssetDto: BodyType<UpdateMediaAssetDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaAssetDto>({
    url: `/api/v1/workspaces/${id}/media/${assetId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateMediaAssetDto,
    signal,
  });
};

export const getUpdateMediaAssetMutationKey = () => ['updateMediaAsset'] as const;

export const getUpdateMediaAssetMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMediaAsset>>,
    TError,
    UpdateMediaAssetMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMediaAsset>>,
  TError,
  UpdateMediaAssetMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateMediaAssetMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMediaAsset>>,
    UpdateMediaAssetMutationVariables
  > = (props) => {
    const { id, assetId, data } = props ?? {};

    return updateMediaAsset(id, assetId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMediaAssetMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMediaAsset>>
>;
export type UpdateMediaAssetMutationBody = BodyType<UpdateMediaAssetDto>;
export type UpdateMediaAssetMutationError = ErrorType<ApiErrorDto>;
export type UpdateMediaAssetMutationVariables = {
  id: string;
  assetId: string;
  data: BodyType<UpdateMediaAssetDto>;
};

/**
 * @summary Rename an asset, replace its tags or change who it belongs to.
 */
export const useUpdateMediaAsset = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateMediaAsset>>,
      TError,
      UpdateMediaAssetMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateMediaAsset>>,
  TError,
  UpdateMediaAssetMutationVariables,
  TContext
> => {
  return useMutation(getUpdateMediaAssetMutationOptions(options), queryClient);
};
/**
 * @summary Write a caption by hand (e.g. for a bank of on-slide texts), optionally into a collection.
 */
export const createCaptionAsset = (
  id: string,
  createCaptionDto: BodyType<CreateCaptionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaAssetDto>({
    url: `/api/v1/workspaces/${id}/media/captions`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createCaptionDto,
    signal,
  });
};

export const getCreateCaptionAssetMutationKey = () => ['createCaptionAsset'] as const;

export const getCreateCaptionAssetMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCaptionAsset>>,
    TError,
    CreateCaptionAssetMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCaptionAsset>>,
  TError,
  CreateCaptionAssetMutationVariables,
  TContext
> => {
  const mutationKey = getCreateCaptionAssetMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCaptionAsset>>,
    CreateCaptionAssetMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createCaptionAsset(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCaptionAssetMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCaptionAsset>>
>;
export type CreateCaptionAssetMutationBody = BodyType<CreateCaptionDto>;
export type CreateCaptionAssetMutationError = ErrorType<ApiErrorDto>;
export type CreateCaptionAssetMutationVariables = {
  id: string;
  data: BodyType<CreateCaptionDto>;
};

/**
 * @summary Write a caption by hand (e.g. for a bank of on-slide texts), optionally into a collection.
 */
export const useCreateCaptionAsset = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createCaptionAsset>>,
      TError,
      CreateCaptionAssetMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createCaptionAsset>>,
  TError,
  CreateCaptionAssetMutationVariables,
  TContext
> => {
  return useMutation(getCreateCaptionAssetMutationOptions(options), queryClient);
};
/**
 * @summary Save one scene of a saved reel as a clip: cut from its video, with the
scene's frame, on-screen text and speech.
 */
export const saveSceneClip = (
  id: string,
  assetId: string,
  scene: number,
  saveSceneClipDto: BodyType<SaveSceneClipDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaAssetDto>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/scenes/${scene}/clip`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: saveSceneClipDto,
    signal,
  });
};

export const getSaveSceneClipMutationKey = () => ['saveSceneClip'] as const;

export const getSaveSceneClipMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveSceneClip>>,
    TError,
    SaveSceneClipMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveSceneClip>>,
  TError,
  SaveSceneClipMutationVariables,
  TContext
> => {
  const mutationKey = getSaveSceneClipMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveSceneClip>>,
    SaveSceneClipMutationVariables
  > = (props) => {
    const { id, assetId, scene, data } = props ?? {};

    return saveSceneClip(id, assetId, scene, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveSceneClipMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveSceneClip>>
>;
export type SaveSceneClipMutationBody = BodyType<SaveSceneClipDto>;
export type SaveSceneClipMutationError = ErrorType<ApiErrorDto>;
export type SaveSceneClipMutationVariables = {
  id: string;
  assetId: string;
  scene: number;
  data: BodyType<SaveSceneClipDto>;
};

/**
 * @summary Save one scene of a saved reel as a clip: cut from its video, with the
scene's frame, on-screen text and speech.
 */
export const useSaveSceneClip = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveSceneClip>>,
      TError,
      SaveSceneClipMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveSceneClip>>,
  TError,
  SaveSceneClipMutationVariables,
  TContext
> => {
  return useMutation(getSaveSceneClipMutationOptions(options), queryClient);
};
/**
 * @summary Remake one scene of a saved reel as an AI video: its frame redrawn with a
new person and no text, then animated by the picked model. It's made in
the background; the result lands in the library as a clip.
 */
export const replicateScene = (
  id: string,
  assetId: string,
  scene: number,
  replicateSceneDto: BodyType<ReplicateSceneDto>,
  signal?: AbortSignal,
) => {
  return customInstance<SceneReplicaDto>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/scenes/${scene}/replicate`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: replicateSceneDto,
    signal,
  });
};

export const getReplicateSceneMutationKey = () => ['replicateScene'] as const;

export const getReplicateSceneMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof replicateScene>>,
    TError,
    ReplicateSceneMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof replicateScene>>,
  TError,
  ReplicateSceneMutationVariables,
  TContext
> => {
  const mutationKey = getReplicateSceneMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof replicateScene>>,
    ReplicateSceneMutationVariables
  > = (props) => {
    const { id, assetId, scene, data } = props ?? {};

    return replicateScene(id, assetId, scene, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReplicateSceneMutationResult = NonNullable<
  Awaited<ReturnType<typeof replicateScene>>
>;
export type ReplicateSceneMutationBody = BodyType<ReplicateSceneDto>;
export type ReplicateSceneMutationError = ErrorType<ApiErrorDto>;
export type ReplicateSceneMutationVariables = {
  id: string;
  assetId: string;
  scene: number;
  data: BodyType<ReplicateSceneDto>;
};

/**
 * @summary Remake one scene of a saved reel as an AI video: its frame redrawn with a
new person and no text, then animated by the picked model. It's made in
the background; the result lands in the library as a clip.
 */
export const useReplicateScene = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof replicateScene>>,
      TError,
      ReplicateSceneMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof replicateScene>>,
  TError,
  ReplicateSceneMutationVariables,
  TContext
> => {
  return useMutation(getReplicateSceneMutationOptions(options), queryClient);
};
/**
 * @summary A reel's scene replicas, newest first, made or still being made.
 */
export const listSceneReplicas = (id: string, assetId: string, signal?: AbortSignal) => {
  return customInstance<SceneReplicaDto[]>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/scene-replicas`,
    method: 'GET',
    signal,
  });
};

export const getListSceneReplicasQueryKey = (id: string, assetId: string) => {
  return [`/api/v1/workspaces/${id}/media/${assetId}/scene-replicas`] as const;
};

export const getListSceneReplicasQueryOptions = <
  TData = Awaited<ReturnType<typeof listSceneReplicas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  assetId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSceneReplicasQueryKey(id, assetId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSceneReplicas>>> = ({
    signal,
  }) => listSceneReplicas(id, assetId, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && assetId !== null && assetId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListSceneReplicasQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSceneReplicas>>
>;
export type ListSceneReplicasQueryError = ErrorType<ApiErrorDto>;

export function useListSceneReplicas<
  TData = Awaited<ReturnType<typeof listSceneReplicas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  assetId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSceneReplicas>>,
          TError,
          Awaited<ReturnType<typeof listSceneReplicas>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSceneReplicas<
  TData = Awaited<ReturnType<typeof listSceneReplicas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  assetId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSceneReplicas>>,
          TError,
          Awaited<ReturnType<typeof listSceneReplicas>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSceneReplicas<
  TData = Awaited<ReturnType<typeof listSceneReplicas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  assetId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A reel's scene replicas, newest first, made or still being made.
 */

export function useListSceneReplicas<
  TData = Awaited<ReturnType<typeof listSceneReplicas>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  assetId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSceneReplicas>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSceneReplicasQueryOptions(id, assetId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete assets and their files, for good.
 */
export const downloadMediaAsset = (id: string, assetId: string, signal?: AbortSignal) => {
  return customInstance<MediaDownloadLinkDto>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/download`,
    method: 'POST',
    signal,
  });
};

export const getDownloadMediaAssetMutationKey = () => ['downloadMediaAsset'] as const;

export const getDownloadMediaAssetMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof downloadMediaAsset>>,
    TError,
    DownloadMediaAssetMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof downloadMediaAsset>>,
  TError,
  DownloadMediaAssetMutationVariables,
  TContext
> => {
  const mutationKey = getDownloadMediaAssetMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof downloadMediaAsset>>,
    DownloadMediaAssetMutationVariables
  > = (props) => {
    const { id, assetId } = props ?? {};

    return downloadMediaAsset(id, assetId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DownloadMediaAssetMutationResult = NonNullable<
  Awaited<ReturnType<typeof downloadMediaAsset>>
>;

export type DownloadMediaAssetMutationError = ErrorType<ApiErrorDto>;
export type DownloadMediaAssetMutationVariables = { id: string; assetId: string };

/**
 * @summary Delete assets and their files, for good.
 */
export const useDownloadMediaAsset = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof downloadMediaAsset>>,
      TError,
      DownloadMediaAssetMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof downloadMediaAsset>>,
  TError,
  DownloadMediaAssetMutationVariables,
  TContext
> => {
  return useMutation(getDownloadMediaAssetMutationOptions(options), queryClient);
};
/**
 * @summary Items as one zip: videos, images, a carousel's slides in a folder, captions
as text. `X-Skipped` counts the items that had no file.
 */
export const downloadMedia = (
  id: string,
  downloadMediaDto: BodyType<DownloadMediaDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/media/downloads`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: downloadMediaDto,
    signal,
  });
};

export const getDownloadMediaMutationKey = () => ['downloadMedia'] as const;

export const getDownloadMediaMutationOptions = <
  TError = ErrorType<ApiErrorDto | Blob>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof downloadMedia>>,
    TError,
    DownloadMediaMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof downloadMedia>>,
  TError,
  DownloadMediaMutationVariables,
  TContext
> => {
  const mutationKey = getDownloadMediaMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof downloadMedia>>,
    DownloadMediaMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return downloadMedia(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type DownloadMediaMutationResult = NonNullable<
  Awaited<ReturnType<typeof downloadMedia>>
>;
export type DownloadMediaMutationBody = BodyType<DownloadMediaDto>;
export type DownloadMediaMutationError = ErrorType<ApiErrorDto | Blob>;
export type DownloadMediaMutationVariables = {
  id: string;
  data: BodyType<DownloadMediaDto>;
};

/**
 * @summary Items as one zip: videos, images, a carousel's slides in a folder, captions
as text. `X-Skipped` counts the items that had no file.
 */
export const useDownloadMedia = <
  TError = ErrorType<ApiErrorDto | Blob>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof downloadMedia>>,
      TError,
      DownloadMediaMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof downloadMedia>>,
  TError,
  DownloadMediaMutationVariables,
  TContext
> => {
  return useMutation(getDownloadMediaMutationOptions(options), queryClient);
};
export const deleteMediaAssets = (
  id: string,
  deleteMediaAssetsDto: BodyType<DeleteMediaAssetsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/media/delete`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: deleteMediaAssetsDto,
    signal,
  });
};

export const getDeleteMediaAssetsMutationKey = () => ['deleteMediaAssets'] as const;

export const getDeleteMediaAssetsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMediaAssets>>,
    TError,
    DeleteMediaAssetsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMediaAssets>>,
  TError,
  DeleteMediaAssetsMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteMediaAssetsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMediaAssets>>,
    DeleteMediaAssetsMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return deleteMediaAssets(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMediaAssetsMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMediaAssets>>
>;
export type DeleteMediaAssetsMutationBody = BodyType<DeleteMediaAssetsDto>;
export type DeleteMediaAssetsMutationError = ErrorType<ApiErrorDto>;
export type DeleteMediaAssetsMutationVariables = {
  id: string;
  data: BodyType<DeleteMediaAssetsDto>;
};

export const useDeleteMediaAssets = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteMediaAssets>>,
      TError,
      DeleteMediaAssetsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteMediaAssets>>,
  TError,
  DeleteMediaAssetsMutationVariables,
  TContext
> => {
  return useMutation(getDeleteMediaAssetsMutationOptions(options), queryClient);
};
/**
 * @summary Add and remove tags on many assets at once.
 */
export const tagMediaAssets = (
  id: string,
  tagMediaAssetsDto: BodyType<TagMediaAssetsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/media/tags`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: tagMediaAssetsDto,
    signal,
  });
};

export const getTagMediaAssetsMutationKey = () => ['tagMediaAssets'] as const;

export const getTagMediaAssetsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof tagMediaAssets>>,
    TError,
    TagMediaAssetsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof tagMediaAssets>>,
  TError,
  TagMediaAssetsMutationVariables,
  TContext
> => {
  const mutationKey = getTagMediaAssetsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof tagMediaAssets>>,
    TagMediaAssetsMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return tagMediaAssets(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type TagMediaAssetsMutationResult = NonNullable<
  Awaited<ReturnType<typeof tagMediaAssets>>
>;
export type TagMediaAssetsMutationBody = BodyType<TagMediaAssetsDto>;
export type TagMediaAssetsMutationError = ErrorType<ApiErrorDto>;
export type TagMediaAssetsMutationVariables = {
  id: string;
  data: BodyType<TagMediaAssetsDto>;
};

/**
 * @summary Add and remove tags on many assets at once.
 */
export const useTagMediaAssets = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof tagMediaAssets>>,
      TError,
      TagMediaAssetsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof tagMediaAssets>>,
  TError,
  TagMediaAssetsMutationVariables,
  TContext
> => {
  return useMutation(getTagMediaAssetsMutationOptions(options), queryClient);
};
/**
 * @summary Start an upload: returns short-lived URLs to PUT the file (and a video's
poster frame) to, then call completeMediaUpload.
 */
export const createMediaUpload = (
  id: string,
  createMediaUploadDto: BodyType<CreateMediaUploadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaUploadDto>({
    url: `/api/v1/workspaces/${id}/media/uploads`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createMediaUploadDto,
    signal,
  });
};

export const getCreateMediaUploadMutationKey = () => ['createMediaUpload'] as const;

export const getCreateMediaUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMediaUpload>>,
    TError,
    CreateMediaUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMediaUpload>>,
  TError,
  CreateMediaUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCreateMediaUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMediaUpload>>,
    CreateMediaUploadMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createMediaUpload(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMediaUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMediaUpload>>
>;
export type CreateMediaUploadMutationBody = BodyType<CreateMediaUploadDto>;
export type CreateMediaUploadMutationError = ErrorType<ApiErrorDto>;
export type CreateMediaUploadMutationVariables = {
  id: string;
  data: BodyType<CreateMediaUploadDto>;
};

/**
 * @summary Start an upload: returns short-lived URLs to PUT the file (and a video's
poster frame) to, then call completeMediaUpload.
 */
export const useCreateMediaUpload = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createMediaUpload>>,
      TError,
      CreateMediaUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createMediaUpload>>,
  TError,
  CreateMediaUploadMutationVariables,
  TContext
> => {
  return useMutation(getCreateMediaUploadMutationOptions(options), queryClient);
};
/**
 * @summary Finish an upload once the file is in storage; the asset joins the library.
 */
export const completeMediaUpload = (
  id: string,
  assetId: string,
  completeMediaUploadDto: BodyType<CompleteMediaUploadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaAssetDto>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/complete`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: completeMediaUploadDto,
    signal,
  });
};

export const getCompleteMediaUploadMutationKey = () => ['completeMediaUpload'] as const;

export const getCompleteMediaUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof completeMediaUpload>>,
    TError,
    CompleteMediaUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof completeMediaUpload>>,
  TError,
  CompleteMediaUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCompleteMediaUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof completeMediaUpload>>,
    CompleteMediaUploadMutationVariables
  > = (props) => {
    const { id, assetId, data } = props ?? {};

    return completeMediaUpload(id, assetId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CompleteMediaUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof completeMediaUpload>>
>;
export type CompleteMediaUploadMutationBody = BodyType<CompleteMediaUploadDto>;
export type CompleteMediaUploadMutationError = ErrorType<ApiErrorDto>;
export type CompleteMediaUploadMutationVariables = {
  id: string;
  assetId: string;
  data: BodyType<CompleteMediaUploadDto>;
};

/**
 * @summary Finish an upload once the file is in storage; the asset joins the library.
 */
export const useCompleteMediaUpload = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof completeMediaUpload>>,
      TError,
      CompleteMediaUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof completeMediaUpload>>,
  TError,
  CompleteMediaUploadMutationVariables,
  TContext
> => {
  return useMutation(getCompleteMediaUploadMutationOptions(options), queryClient);
};
/**
 * @summary Abandon an unfinished upload.
 */
export const cancelMediaUpload = (id: string, assetId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/media/${assetId}/upload`,
    method: 'DELETE',
    signal,
  });
};

export const getCancelMediaUploadMutationKey = () => ['cancelMediaUpload'] as const;

export const getCancelMediaUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelMediaUpload>>,
    TError,
    CancelMediaUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelMediaUpload>>,
  TError,
  CancelMediaUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCancelMediaUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelMediaUpload>>,
    CancelMediaUploadMutationVariables
  > = (props) => {
    const { id, assetId } = props ?? {};

    return cancelMediaUpload(id, assetId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelMediaUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelMediaUpload>>
>;

export type CancelMediaUploadMutationError = ErrorType<ApiErrorDto>;
export type CancelMediaUploadMutationVariables = { id: string; assetId: string };

/**
 * @summary Abandon an unfinished upload.
 */
export const useCancelMediaUpload = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelMediaUpload>>,
      TError,
      CancelMediaUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof cancelMediaUpload>>,
  TError,
  CancelMediaUploadMutationVariables,
  TContext
> => {
  return useMutation(getCancelMediaUploadMutationOptions(options), queryClient);
};
/**
 * @summary Save a TikTok or Instagram reel or carousel from its link. Fetching runs
in the background: poll getMediaImport until it's DONE (the post is in
the library) or FAILED (with a reason to show).
 */
export const createMediaImport = (
  id: string,
  createMediaImportDto: BodyType<CreateMediaImportDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaImportDto>({
    url: `/api/v1/workspaces/${id}/media/imports`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createMediaImportDto,
    signal,
  });
};

export const getCreateMediaImportMutationKey = () => ['createMediaImport'] as const;

export const getCreateMediaImportMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMediaImport>>,
    TError,
    CreateMediaImportMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMediaImport>>,
  TError,
  CreateMediaImportMutationVariables,
  TContext
> => {
  const mutationKey = getCreateMediaImportMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMediaImport>>,
    CreateMediaImportMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createMediaImport(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMediaImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMediaImport>>
>;
export type CreateMediaImportMutationBody = BodyType<CreateMediaImportDto>;
export type CreateMediaImportMutationError = ErrorType<ApiErrorDto>;
export type CreateMediaImportMutationVariables = {
  id: string;
  data: BodyType<CreateMediaImportDto>;
};

/**
 * @summary Save a TikTok or Instagram reel or carousel from its link. Fetching runs
in the background: poll getMediaImport until it's DONE (the post is in
the library) or FAILED (with a reason to show).
 */
export const useCreateMediaImport = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createMediaImport>>,
      TError,
      CreateMediaImportMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createMediaImport>>,
  TError,
  CreateMediaImportMutationVariables,
  TContext
> => {
  return useMutation(getCreateMediaImportMutationOptions(options), queryClient);
};
/**
 * @summary Where a save from a link has got to.
 */
export const getMediaImport = (id: string, importId: string, signal?: AbortSignal) => {
  return customInstance<MediaImportDto>({
    url: `/api/v1/workspaces/${id}/media/imports/${importId}`,
    method: 'GET',
    signal,
  });
};

export const getGetMediaImportQueryKey = (id: string, importId: string) => {
  return [`/api/v1/workspaces/${id}/media/imports/${importId}`] as const;
};

export const getGetMediaImportQueryOptions = <
  TData = Awaited<ReturnType<typeof getMediaImport>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  importId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMediaImportQueryKey(id, importId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMediaImport>>> = ({
    signal,
  }) => getMediaImport(id, importId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && importId !== null && importId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetMediaImportQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMediaImport>>
>;
export type GetMediaImportQueryError = ErrorType<ApiErrorDto>;

export function useGetMediaImport<
  TData = Awaited<ReturnType<typeof getMediaImport>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  importId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMediaImport>>,
          TError,
          Awaited<ReturnType<typeof getMediaImport>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMediaImport<
  TData = Awaited<ReturnType<typeof getMediaImport>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  importId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMediaImport>>,
          TError,
          Awaited<ReturnType<typeof getMediaImport>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMediaImport<
  TData = Awaited<ReturnType<typeof getMediaImport>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  importId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Where a save from a link has got to.
 */

export function useGetMediaImport<
  TData = Awaited<ReturnType<typeof getMediaImport>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  importId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMediaImport>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMediaImportQueryOptions(id, importId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
